import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { isAdminUser, ADMIN_EMAILS, ADMIN_NAMES } from '../config/adminConfig';

const AdminManagement: React.FC = () => {
  const { user } = useAuth();
  const [adminEmails, setAdminEmails] = useState<string[]>(ADMIN_EMAILS);
  const [adminNames, setAdminNames] = useState<string[]>(ADMIN_NAMES);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newAdminType, setNewAdminType] = useState<'email' | 'name'>('email');
  const [newAdminValue, setNewAdminValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  // 檢查是否為管理員
  const isAdmin = isAdminUser(user?.email, user?.name);

  const handleAddAdmin = () => {
    if (!newAdminValue.trim()) {
      setError('請輸入有效的值');
      return;
    }

    if (newAdminType === 'email') {
      if (!newAdminValue.includes('@')) {
        setError('請輸入有效的email地址');
        return;
      }
      if (adminEmails.includes(newAdminValue)) {
        setError('此email已存在');
        return;
      }
      setAdminEmails([...adminEmails, newAdminValue]);
    } else {
      if (adminNames.includes(newAdminValue)) {
        setError('此名稱已存在');
        return;
      }
      setAdminNames([...adminNames, newAdminValue]);
    }

    setNewAdminValue('');
    setError(null);
    setShowAddDialog(false);
  };

  const handleRemoveAdmin = (type: 'email' | 'name', value: string) => {
    if (type === 'email') {
      setAdminEmails(adminEmails.filter(email => email !== value));
    } else {
      setAdminNames(adminNames.filter(name => name !== value));
    }
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="error" sx={{ maxWidth: 400, mx: 'auto' }}>
          <Typography variant="h6">權限不足</Typography>
          <Typography>您沒有權限訪問此頁面</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* 頁面標題 */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <AdminIcon sx={{ fontSize: 32, color: '#d32f2f', mr: 1 }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          管理員管理
        </Typography>
      </Box>

      {/* 錯誤提示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* 當前管理員列表 */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            當前管理員列表
          </Typography>
        </Box>
        
        <List>
          <ListItem>
            <ListItemText 
              primary="Email 管理員" 
              secondary={`共 ${adminEmails.length} 個`}
            />
            <ListItemSecondaryAction>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  setNewAdminType('email');
                  setShowAddDialog(true);
                }}
              >
                添加
              </Button>
            </ListItemSecondaryAction>
          </ListItem>
          
          {adminEmails.map((email, index) => (
            <ListItem key={`email-${index}`} sx={{ pl: 4 }}>
              <ListItemText 
                primary={email}
                secondary="Email 管理員"
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  onClick={() => handleRemoveAdmin('email', email)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}

          <Divider />

          <ListItem>
            <ListItemText 
              primary="名稱管理員" 
              secondary={`共 ${adminNames.length} 個`}
            />
            <ListItemSecondaryAction>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  setNewAdminType('name');
                  setShowAddDialog(true);
                }}
              >
                添加
              </Button>
            </ListItemSecondaryAction>
          </ListItem>
          
          {adminNames.map((name, index) => (
            <ListItem key={`name-${index}`} sx={{ pl: 4 }}>
              <ListItemText 
                primary={name}
                secondary="名稱管理員"
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  onClick={() => handleRemoveAdmin('name', name)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* 添加管理員對話框 */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>添加新管理員</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              管理員類型
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
              <Chip
                label="Email 管理員"
                color={newAdminType === 'email' ? 'primary' : 'default'}
                onClick={() => setNewAdminType('email')}
                clickable
              />
              <Chip
                label="名稱管理員"
                color={newAdminType === 'name' ? 'primary' : 'default'}
                onClick={() => setNewAdminType('name')}
                clickable
              />
            </Box>
            
            <TextField
              fullWidth
              label={newAdminType === 'email' ? 'Email 地址' : '用戶名稱'}
              value={newAdminValue}
              onChange={(e) => setNewAdminValue(e.target.value)}
              placeholder={newAdminType === 'email' ? 'admin@example.com' : 'admin'}
              type={newAdminType === 'email' ? 'email' : 'text'}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>取消</Button>
          <Button onClick={handleAddAdmin} variant="contained">添加</Button>
        </DialogActions>
      </Dialog>

      {/* 注意事項 */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
          注意事項：
        </Typography>
        <Typography variant="body2" component="div">
          • 此頁面僅用於顯示當前管理員配置
          • 實際的管理員權限需要在 <code>src/config/adminConfig.ts</code> 中修改
          • 修改後需要重新部署應用程序才能生效
          • Email 管理員優先級高於名稱管理員
        </Typography>
      </Alert>
    </Box>
  );
};

export default AdminManagement;
